import { extent } from 'd3-array';
import { area, curveMonotoneX, line } from 'd3-shape';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { LoadingWave } from '@/components/Charts/LoadingWave';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import { ChartOptionSelector } from '@/ui/Charts/ChartOptionSelector';

import { varMappings } from '@/utils/generated/varMappings';
import type { ChartPeriod, WalletChartPoint } from '@/utils/zerion/types';

interface PortfolioChartProps {
  data: WalletChartPoint[];
  /** Width - if undefined, uses container width */
  width?: number;
  height?: number;
  /** Portfolio change percentage to determine color */
  changePercent?: number;
  /** Current time period */
  period: ChartPeriod;
  /** Callback when period changes */
  onPeriodChange: (period: ChartPeriod) => void;
  /** Is data loading */
  isLoading?: boolean;
  /** Value block rendered top-left, opposite the timeframe selector */
  summary?: ReactNode;
}

interface TooltipData {
  value: number;
  timestamp: number;
  x: number;
  y: number;
}

const PERIOD_OPTIONS: { label: string; value: ChartPeriod }[] = [
  { label: '1D', value: 'day' },
  { label: '1W', value: 'week' },
  { label: '1M', value: 'month' },
  { label: '1Y', value: 'year' },
  { label: 'All', value: 'max' },
];

function ChartHeader({
  summary,
  period,
  onPeriodChange,
}: {
  summary?: ReactNode;
  period: ChartPeriod;
  onPeriodChange: (period: ChartPeriod) => void;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-4 pl-5 pr-4 tablet:pl-7.5">
      <div>{summary}</div>
      <ChartOptionSelector
        ariaLabel="Select chart time range"
        options={PERIOD_OPTIONS}
        value={period}
        onChange={onPeriodChange}
      />
    </div>
  );
}

function formatAxisValue(value: number, tickStep?: number): string {
  // Determine precision based on tick step to avoid duplicate labels
  const step = tickStep ?? 0;

  if (value >= 1_000_000) {
    const decimals = step < 100_000 ? 2 : step < 500_000 ? 1 : 1;
    return `${(value / 1_000_000).toFixed(decimals)}M`;
  }
  if (value >= 1_000) {
    const decimals = step < 100 ? 2 : step < 500 ? 1 : 0;
    return `${(value / 1_000).toFixed(decimals)}k`;
  }
  return value.toFixed(0);
}

// Convert timestamp to milliseconds if it's in seconds (Unix epoch)
function toMilliseconds(timestamp: number): number {
  // If timestamp is less than year 2000 in ms, it's probably in seconds
  return timestamp < 1e12 ? timestamp * 1000 : timestamp;
}

function formatTooltipDate(timestamp: number, period: ChartPeriod): string {
  const date = new Date(toMilliseconds(timestamp));
  const options: Intl.DateTimeFormatOptions =
    period === 'day' || period === 'hour'
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  return date.toLocaleDateString('en-US', options);
}

function formatXAxisLabel(timestamp: number, period: ChartPeriod, dataSpanDays?: number): string {
  const date = new Date(toMilliseconds(timestamp));

  if (period === 'day' || period === 'hour') {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  if (period === 'year') {
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }

  if (period === 'max' && dataSpanDays !== undefined) {
    if (dataSpanDays > 365) {
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    if (dataSpanDays > 60) {
      return date.toLocaleDateString('en-US', { month: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function PortfolioChart({
  data,
  width: propWidth,
  height = 300,
  changePercent,
  period,
  onPeriodChange,
  isLoading,
  summary,
}: PortfolioChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [containerWidth, setContainerWidth] = useState(propWidth ?? 800);
  const { formatFiat } = useFiatDisplay();

  useEffect(() => {
    if (propWidth !== undefined) {
      setContainerWidth(propWidth);
      return;
    }

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [propWidth]);

  const width = containerWidth;

  const margin = { top: 20, right: 50, bottom: 40, left: 0 };
  const chartWidth = Math.max(width - margin.left - margin.right, 0);
  const chartHeight = height - margin.top - margin.bottom;

  const chartColor = useMemo(() => {
    if (changePercent !== undefined) {
      if (changePercent > 0) return varMappings['token-green-900'];
      if (changePercent < 0) return varMappings['token-red-900'];
    } else if (data.length >= 2) {
      const firstValue = data[0].value;
      const lastValue = data[data.length - 1].value;
      if (lastValue > firstValue) return varMappings['token-green-900'];
      if (lastValue < firstValue) return varMappings['token-red-900'];
    }
    return varMappings['token-gray-600'];
  }, [data, changePercent]);

  const { linePath, areaPath, xScale, yScale, xTicks, yTicks } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        linePath: '',
        areaPath: '',
        xScale: () => 0,
        yScale: () => 0,
        xTicks: [] as { value: number; label: string }[],
        yTicks: [] as { value: number; label: string }[],
      };
    }

    const values = data.map((d) => d.value);
    const timestamps = data.map((d) => d.timestamp);

    const [minY, maxY] = extent(values) as [number, number];
    const [minX, maxX] = extent(timestamps) as [number, number];

    const yRange = maxY - minY || 1;
    const yPadding = yRange * 0.1;
    const yMin = minY - yPadding;
    const yMax = maxY + yPadding;

    const xScaleFn = (timestamp: number) =>
      ((timestamp - minX) / (maxX - minX || 1)) * chartWidth + margin.left;
    const yScaleFn = (value: number) =>
      chartHeight - ((value - yMin) / (yMax - yMin || 1)) * chartHeight + margin.top;

    const lineGenerator = line<WalletChartPoint>()
      .x((d) => xScaleFn(d.timestamp))
      .y((d) => yScaleFn(d.value))
      .curve(curveMonotoneX);

    const areaGenerator = area<WalletChartPoint>()
      .x((d) => xScaleFn(d.timestamp))
      .y0(chartHeight + margin.top)
      .y1((d) => yScaleFn(d.value))
      .curve(curveMonotoneX);

    const lineP = lineGenerator(data) || '';
    const areaP = areaGenerator(data) || '';

    const dataSpanMs = toMilliseconds(maxX) - toMilliseconds(minX);
    const dataSpanDays = dataSpanMs / (1000 * 60 * 60 * 24);

    // Pick fewer ticks at narrow widths so date labels don't overlap.
    const xTickCount = chartWidth < 480 ? 4 : chartWidth < 700 ? 5 : 7;
    const xTickStep = (maxX - minX) / (xTickCount - 1);
    const xTicksArr = Array.from({ length: xTickCount }, (_, i) => {
      const timestamp = minX + i * xTickStep;
      return {
        value: xScaleFn(timestamp),
        label: formatXAxisLabel(timestamp, period, dataSpanDays),
      };
    });

    const yTickCount = 5;
    const yTickStep = (yMax - yMin) / (yTickCount - 1);
    const yTicksArr = Array.from({ length: yTickCount }, (_, i) => yMin + i * yTickStep)
      .filter((value) => value >= 0)
      .map((value) => ({
        value: yScaleFn(value),
        label: formatAxisValue(value, yTickStep),
      }));

    return {
      linePath: lineP,
      areaPath: areaP,
      xScale: xScaleFn,
      yScale: yScaleFn,
      xTicks: xTicksArr,
      yTicks: yTicksArr,
    };
  }, [data, chartWidth, chartHeight, margin.left, margin.top, period]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGElement>) => {
      if (!data || data.length === 0 || !svgRef.current) return;

      const svgRect = svgRef.current.getBoundingClientRect();
      const mouseX = event.clientX - svgRect.left;

      let closestPoint = data[0];
      let closestDistance = Infinity;

      for (const point of data) {
        const pointX = xScale(point.timestamp);
        const distance = Math.abs(pointX - mouseX);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPoint = point;
        }
      }

      if (closestPoint) {
        setTooltip({
          value: closestPoint.value,
          timestamp: closestPoint.timestamp,
          x: xScale(closestPoint.timestamp),
          y: yScale(closestPoint.value),
        });
      }
    },
    [data, xScale, yScale],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  if (isLoading) {
    return (
      <div ref={containerRef} className="flex flex-col w-full">
        <ChartHeader period={period} onPeriodChange={onPeriodChange} summary={summary} />
        <div className="relative w-full overflow-hidden" style={{ height }}>
          <LoadingWave />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div ref={containerRef} className="flex flex-col w-full">
        <ChartHeader period={period} onPeriodChange={onPeriodChange} summary={summary} />
        <div
          style={{ height }}
          className="w-full bg-token-alphalight-100 rounded-lg flex items-center justify-center"
        >
          <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>
            No chart data available
          </span>
        </div>
      </div>
    );
  }

  const gradientId = `portfolio-chart-gradient-${period}`;

  return (
    <div ref={containerRef} className="flex flex-col w-full">
      <ChartHeader period={period} onPeriodChange={onPeriodChange} summary={summary} />

      <div className="relative flex-1">
        <svg ref={svgRef} width={width} height={height} className="overflow-visible">
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={chartColor} stopOpacity="0.3" />
              <stop offset="100%" stopColor={chartColor} stopOpacity="0.02" />
            </linearGradient>

            <linearGradient id="crosshair-v-fade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="black" />
              <stop offset="15%" stopColor="white" />
              <stop offset="85%" stopColor="white" />
              <stop offset="100%" stopColor="black" />
            </linearGradient>
            <linearGradient id="crosshair-h-fade" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="black" />
              <stop offset="12%" stopColor="white" />
              <stop offset="88%" stopColor="white" />
              <stop offset="100%" stopColor="black" />
            </linearGradient>
            <mask
              id="crosshair-v-mask"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={width}
              height={height}
            >
              <rect
                x={0}
                y={margin.top}
                width={width}
                height={chartHeight}
                fill="url(#crosshair-v-fade)"
              />
            </mask>
            <mask
              id="crosshair-h-mask"
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={width}
              height={height}
            >
              <rect
                x={margin.left}
                y={0}
                width={chartWidth}
                height={height}
                fill="url(#crosshair-h-fade)"
              />
            </mask>
          </defs>

          <path d={areaPath} fill={`url(#${gradientId})`} pointerEvents="none" />

          <path
            d={linePath}
            fill="none"
            stroke={chartColor}
            strokeWidth={2}
            strokeLinecap="round"
            pointerEvents="none"
          />

          {/* Invisible hit area for chart hover — covers only the data region */}
          <rect
            x={margin.left}
            y={margin.top}
            width={chartWidth}
            height={chartHeight}
            fill="transparent"
            className="cursor-crosshair"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />

          {yTicks.map((tick, i) => (
            <text
              key={`y-label-${i}`}
              x={width - margin.right + 8}
              y={tick.value}
              dy="0.35em"
              className={fontStyles({ variant: 'CAPTION' })}
              fill="var(--token-color-gray-700)"
              textAnchor="start"
            >
              {tick.label}
            </text>
          ))}

          {xTicks.map((tick, i) => {
            const isFirst = i === 0;
            const isLast = i === xTicks.length - 1;
            return (
              <text
                key={`x-label-${i}`}
                x={isFirst ? 16 : tick.value}
                y={height - 10}
                className={fontStyles({ variant: 'CAPTION' })}
                fill="var(--token-color-gray-700)"
                textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
              >
                {tick.label}
              </text>
            );
          })}

          {tooltip && (
            <g pointerEvents="none">
              <line
                x1={tooltip.x}
                x2={tooltip.x}
                y1={margin.top}
                y2={margin.top + chartHeight}
                stroke="var(--token-color-alphalight-400)"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                mask="url(#crosshair-v-mask)"
              />
              <line
                x1={margin.left}
                x2={margin.left + chartWidth}
                y1={tooltip.y}
                y2={tooltip.y}
                stroke="var(--token-color-alphalight-400)"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                mask="url(#crosshair-h-mask)"
              />
              <circle
                cx={tooltip.x}
                cy={tooltip.y}
                r={13}
                fill="var(--token-color-special-background-overlay)"
              />
              <circle cx={tooltip.x} cy={tooltip.y} r={6} fill="var(--token-color-gray-1600)" />
            </g>
          )}
        </svg>

        {tooltip && (
          <div
            className="flex flex-col gap-y-1.5 absolute pointer-events-none bg-special-background-tooltip border border-token-gray-300 rounded-[18px] px-4 py-3 shadow-lg"
            style={{
              left: tooltip.x,
              top: tooltip.y - 21,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div
              className={fontStyles({
                variant: 'LABEL_LG',
                class: 'text-token-gray-1600 font-semibold text-center',
              })}
            >
              {formatFiat(tooltip.value)}
            </div>
            <div
              className={fontStyles({
                variant: 'CAPTION',
                class: 'text-token-gray-700 text-center whitespace-nowrap',
              })}
            >
              {formatTooltipDate(tooltip.timestamp, period)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function PortfolioChartSkeleton({
  height = 300,
  period,
  onPeriodChange,
  summary,
}: {
  width?: number;
  height?: number;
  period: ChartPeriod;
  onPeriodChange: (period: ChartPeriod) => void;
  summary?: ReactNode;
}) {
  return (
    <div className="flex flex-col w-full">
      <ChartHeader period={period} onPeriodChange={onPeriodChange} summary={summary} />
      <div className="relative w-full overflow-hidden" style={{ height }}>
        <LoadingWave />
      </div>
    </div>
  );
}
